import { Globe, Check } from 'lucide-react'
import { useState, useRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useSubmit } from 'react-router'
import { cn } from '#app/utils/misc'
import { Button } from './ui/button'
import { Tooltip, TooltipContent, TooltipTrigger } from './ui/tooltip'

const languages = [
  { code: 'en', name: 'English', flag: '🇬🇧' },
  { code: 'nl', name: 'Nederlands', flag: '🇳🇱' },
]

interface LanguageSwitcherProps {
  isCollapsed?: boolean
}

export function LanguageSwitcherSidebar({ isCollapsed = false }: LanguageSwitcherProps) {
  const { i18n, ready } = useTranslation()
  const submit = useSubmit()
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  const handleLanguageChange = (langCode: string) => {
    if (!i18n) return
    
    // Change language in i18n
    i18n.changeLanguage(langCode).catch((error) => {
      console.error('Failed to change language:', error)
    })
    
    // Update cookie by submitting a form
    const formData = new FormData()
    formData.append('lng', langCode)
    formData.append('_action', 'setLanguage')
    void submit(formData, { method: 'post', action: '/resources/set-language' })
    
    setIsOpen(false)
  }

  // Determine current language with proper fallback
  const currentLang = i18n?.language || 'en'
  const currentLanguage = languages.find(lang => lang.code === currentLang) || languages[0]

  // Handle click outside - moved before conditional return
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Check if i18n is ready
  if (!ready || !i18n) {
    // Return a placeholder button while loading
    return (
      <Button 
        variant="ghost" 
        size={isCollapsed ? "icon" : "sm"}
        className={cn(
          "w-full justify-start gap-2",
          isCollapsed && "justify-center px-0"
        )}
        disabled
      >
        <Globe className="h-4 w-4" />
        {!isCollapsed && <span>Loading...</span>}
      </Button>
    )
  }

  const buttonContent = (
    <Button 
      variant="ghost" 
      size={isCollapsed ? "icon" : "sm"}
      className={cn(
        "w-full justify-start gap-2",
        isCollapsed && "justify-center px-0"
      )}
      onClick={() => setIsOpen(!isOpen)}
    >
      <Globe className="h-4 w-4" />
      {!isCollapsed && (
        <>
          <span>{currentLanguage?.flag || '🌐'}</span>
          <span className="flex-1 text-left">{currentLanguage?.name || 'Language'}</span>
        </>
      )}
    </Button>
  )

  return (
    <div className="relative w-full" ref={dropdownRef}>
      {isCollapsed ? (
        <Tooltip delayDuration={0}>
          <TooltipTrigger asChild>
            {buttonContent}
          </TooltipTrigger>
          <TooltipContent side="right">
            <p>Taal: {currentLanguage?.name || 'Language'}</p>
          </TooltipContent>
        </Tooltip>
      ) : (
        buttonContent
      )}
      
      {isOpen && (
        <div className={cn(
          "absolute bottom-full mb-2 w-[200px] rounded-md border bg-popover p-1 shadow-md z-50",
          isCollapsed ? "left-full ml-2" : "left-0"
        )}>
          {languages.map((lang) => (
            <button
              key={lang.code}
              onClick={() => handleLanguageChange(lang.code)}
              className={cn(
                "flex w-full items-center rounded-sm px-2 py-1.5 text-sm transition-colors hover:bg-accent hover:text-accent-foreground",
                "cursor-pointer outline-none"
              )}
            >
              <span className="mr-2">{lang.flag}</span>
              <span className="flex-1 text-left">{lang.name}</span>
              {currentLang === lang.code && (
                <Check className="h-4 w-4 text-primary" />
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}